<template>
  <div id="payment-terms">
    <div class="content">
      <h2 class="page-title">Information on Fees, Online Admissions &amp; Payment Terms</h2>
      <About :message="terms" />
    </div>
  </div>
</template>

<script>
import About from '@/components/About'

export default {
  name: 'PaymentTerms',
  components: {
    About
  },
  data () {
    return {
      terms: `<p class="doc-meta">eCommerce Merchant Facility Compliance Policy | Target Audience: Acquiring Bank, eCentric Payment Solutions &amp; Ed-Admin Portal<br>Effective Date: August 2026 | Version 2.0 (Online Admissions &amp; Electronic Acceptance)</p>

#### VISA & Mastercard Merchant Compliance Summary

This document satisfies all mandatory credit card merchant acquiring requirements established by South African banking institutions and card schemes (VISA/Mastercard):

- Terms and Conditions (Sections 1 & 2)
- Digital Service Delivery & Proof of Transaction (Section 4)
- Return & Refund Policy (Section 5)
- Cancellation Policy (Section 6)
- Governing Law & South African Jurisdiction (Section 7)
- Electronic Consent & Online Terms Acceptance (Section 8)

### 1. Preamble & Regulatory Framework

The admission policy of Mitchell House is determined by the governing body of the school in terms of section 5 (5) of the South African Schools' Act, 1996 (No. 84 of 1996). The policy is consistent with the Constitution of the Republic of South Africa, the South African Schools' Act, 1996, and applicable provincial law.

Mitchell House is a Christian School. Pupils of all religions are, however, welcome at Mitchell House, provided they attend all religious services and lessons as integrated into the school curriculum.

Mitchell House reserves the right to check and confirm any information supplied on the online application form and may make any enquiries deemed necessary, including general credit checks and verifying creditworthiness with the last school attended.

### 2. Online Admission Criteria & Enrolment Terms

To be considered for admission at Mitchell House, the following criteria must be completed in full via the online admissions portal:

- **Online Application Form:** Completion in full of the official Online Application for Admission via the Ed-Admin portal, including all required digital document uploads.
- **Placement Assessment:** Successful completion of the placement assessment as well as subject compatibility (Language of instruction is English; First Additional Languages offered are Afrikaans or Sepedi).
- **Credit Rating:** A positive credit rating following background financial verification.
- **Academic & Conduct Record:** Previous school reports indicating a positive work ethic, good disciplinary record, and sustained parental support.
- **Age Requirement:** The applicant is not more than one year out of the Mitchell House age norm for the targeted grade.
- **Application Fee:** Payment of the mandatory non-refundable application processing fee via the secure payment portal.

#### Successful Online Applications

All successful applicants will be offered a place in writing via email. In order to secure the offered place at Mitchell House, the following requirements must be completed online within twenty-one (21) days of written notice:

- Electronic acceptance of the offer via the Ed-Admin Parent Portal.
- Submission of the completed Enrolment Agreement.
- Submission of debit order authorization details.
- Full online payment of all required admission fees and registration deposits by the specified date.

If all conditions to secure acceptance are not met online within twenty-one (21) days of acceptance being granted, the offer of acceptance may be cancelled to assist applicants on the waiting list.

By completing the online application form and accepting a place in the school, parents/guardians agree to adhere to and enforce all academic, sporting, cultural, disciplinary, financial, and uniform requirements as set out in the Code of Conduct and school policies. Particular attention is drawn to the following:

- Promotion requirements across the various school phases.
- Compulsory sports participation from Grade 1 through Grade 11.
- Compliance with the school Dress Code as specified in the Uniform Policy.
- The payment of school fees is compulsory and is payable strictly in advance.

#### Unsuccessful Applications

All unsuccessful applicants will be notified in writing via email. The School is not required to give reasons for non-acceptance, doing so solely at the discretion of the Headmaster or Deputy Head.

### 3. Online Payment Gateway Terms & Gateway Security

Online payments made for school fees, application processing, registration deposits, or portal transactions via the Ed-Admin Parent Portal are securely acquired and processed through eCentric Payment Solutions (Pty) Ltd, an authorized South African payment service provider.

- **Payment Service Provider:** eCentric Payment Solutions (Pty) Ltd.
- **Data Security & PCI Compliance:** All online card transactions are processed in accordance with Payment Card Industry Data Security Standards (PCI-DSS Level 1). Mitchell House does not store, transmit, or process cardholder credit or debit card security numbers on its servers.
- **Accepted Payment Cards:** VISA and Mastercard credit and debit cards.

### 4. Delivery Policy (Digital Service Fulfillment)

As Mitchell House provides educational services, online application processing, and academic instruction rather than physical merchandise, service delivery is fulfilled electronically:

- **Immediate Proof of Transaction:** Upon successful payment authorization via the eCentric gateway, an automated electronic transaction confirmation and official receipt are generated instantly and delivered to the payer's registered email address.
- **Account Statement Updating:** Online payments for tuition fees, application processing, or student account balances are automatically allocated and reflected on the learner's Ed-Admin statement within 1 to 2 business days.

### 5. Return & Refund Policy

Mitchell House maintains clear guidelines regarding financial transactions and fee refunds:

- **Application Fees:** Online application processing fees cover administrative and assessment costs and are non-refundable under any circumstances.
- **Overpayments & Account Credits:** In the event of an accidental duplicate payment or overpayment processed via the online payment portal, the excess funds will automatically be credited directly to the student's fee account.
- **Card Refund Requests:** If a monetary refund back to the cardholder's credit or debit card is required, a formal written request must be submitted to the Mitchell House Bursar's Office (bursar@mitchellhouse.co.za). Validated refund requests will be processed back to the original payment card used within 7 to 14 business days.

### 6. Cancellation Policy

The following cancellation terms apply to online application transactions and enrolment agreements:

- **Online Application Cancellation:** An unsubmitted or unprocessed online application transaction may be cancelled by contacting the Admissions Office in writing prior to administrative processing.
- **Enrolment Contract Cancellation:** Cancellation of enrolment following formal acceptance is governed strictly by the terms of the Mitchell House Enrolment Agreement, which requires one full term's written notice prior to withdrawing a learner, or payment of fees in lieu of notice.

### 7. Merchant Location & Governing Law

In accordance with South African merchant acquiring standards and international card scheme rules:

- **Merchant Outlet Country:** The merchant outlet location at the time of presenting payment options to the cardholder is South Africa.
- **Transaction Currency:** All transactions are billed and processed in South African Rand (ZAR).
- **Governing Law & Jurisdiction:** This website, the Ed-Admin portal, and all online transactions conducted through them are governed by and construed in accordance with the laws of the Republic of South Africa. Any disputes arising in connection with these terms shall be subject to the exclusive jurisdiction of the courts of South Africa.

### 8. Electronic Consent & Terms Acceptance Clause

By completing, submitting, and paying for an online application or fee transaction via the Mitchell House Ed-Admin Parent Portal, you explicitly warrant that all information supplied is true and correct, and you agree to and accept these Terms and Conditions, admission criteria, school rules, and online payment policies in full. Electronic completion and submission of the online application form or portal transaction constitutes full legal acceptance without requiring a physical signature.`
    }
  },
  mounted () {
    this.$store.dispatch('showLoader', false)
  }
}
</script>

<style>
#payment-terms .content {
  margin-top: 20px; }
  #payment-terms .content .page-title {
    color: white;
    padding: 10px;
    background: #009645; }

#payment-terms .about {
  text-align: left; }
  #payment-terms .about .doc-meta {
    font-size: 14px;
    color: #6b6b8a;
    margin-bottom: 25px; }
  #payment-terms .about h3 {
    text-align: left;
    margin: 35px 0 10px;
    padding-left: 12px;
    border-left: 4px solid #009645; }
  #payment-terms .about h4 {
    text-align: left;
    margin: 25px 0 8px;
    color: #009645; }
  #payment-terms .about ul {
    padding-left: 20px;
    margin: 10px 0; }
  #payment-terms .about li {
    display: list-item;
    list-style: disc;
    margin: 8px 0; }
  #payment-terms .about p {
    line-height: 1.6; }

@media screen and (max-width: 1024px) {
  #payment-terms .content h2 {
    margin-top: 0;
  }
  #payment-terms .about {
    text-align: left;
  }
}
</style>
